<template>
  <div class="car-fleet-devices rows mobile-cols">
    <div class="half">
      <DataGrid
          :data-model="dataGridModel"
          :data="filteredVehicles"
          :selected-id="selectedVehicleId"
          @on-select="selectVehicleId"
          allow-select
      />
    </div>
    <div class="half">
      <div class="data-grid-form cols">
        <div class="rows phone-cols name-container">
          <button class="transparent edit-button"
                  @click="edit"
                  :disabled="selectedVehicleId == null"
                  v-if="!editMode">
            <i class="bi bi-pencil-fill"></i>
          </button>
          <div class="half">
            <label for="vehicleNickname">
              {{ $t('settings.car-fleet.form.vehicle-nickname') }}
            </label>
            <input
                id="vehicleNickname"
                class="stretch"
                :class="{'is-invalid': showStateByField('nickname') === false}"
                v-model="nickname"
                :disabled="!editMode"
            />
          </div>
          <div class="half">
            <label for="vehicleIdentifier">
              {{ $t('dashboard.devices.table.uid') }}
            </label>
            <input
                id="vehicleIdentifier"
                class="stretch"
                v-model="identifier"
                disabled
            />
          </div>
        </div>
        <template v-if="userCompany?.allowFullVehicleEdit">
          <div class="rows phone-cols">
            <div class="half">
              <label for="vehicleLicensePlate">
                {{ $t('settings.car-fleet.form.vehicle-license-plate') }}
              </label>
              <input
                  id="vehicleLicensePlate"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('licensePlate') === false}"
                  v-model="licensePlate"
                  :disabled="!editMode"
              />
            </div>
            <div class="half">
              <label for="vehicleDriverName">
                {{ $t('settings.car-fleet.form.vehicle-driver-name') }}
              </label>
              <input
                  id="vehicleDriverName"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('driverName') === false}"
                  v-model="driverName"
                  :disabled="!editMode"
              />
            </div>
          </div>
          <div class="rows phone-cols">
            <div class="half">
              <label for="vehicleMake">
                {{ $t('settings.on-board.devices.form.make') }}
              </label>
              <select
                  id="vehicleMake"
                  :class="['stretch', {'is-invalid': showStateByField('make') === false}]"
                  v-model="make"
                  :disabled="!editMode"
              >
                <option :value="null">{{ $t('settings.generic.select-placeholder') }}</option>
                <option v-for="make in makesAlphabetical" v-bind:key="make.id" :value="make.id">
                  {{ make.name }}
                </option>
              </select>
            </div>
            <div class="half">
              <label for="vehicleModel">
                {{ $t('settings.on-board.devices.form.model') }}
              </label>
              <select
                  id="vehicleModel"
                  :class="['stretch', {'is-invalid': showStateByField('model') === false}]"
                  v-model="model"
                  :disabled="!editMode"
              >
                <option :value="null">{{ $t('settings.generic.select-placeholder') }}</option>
                <option v-for="model in filteredModels" v-bind:key="model.id" :value="model.id">
                  {{ model.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="rows phone-cols">
            <div class="half">
              <label for="vehicleFuelExpenseUrban">
                {{ $t('settings.on-board.devices.form.fuel-expense-urban') }}
              </label>
              <input
                  id="vehicleFuelExpenseUrban"
                  type="number"
                  step="0.1"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('fuelExpenseUrban') === false}"
                  v-model="fuelExpenseUrban"
                  :disabled="!editMode"
              />
            </div>
            <div class="half">
              <label for="vehicleFuelExpenseNonUrban">
                {{ $t('settings.on-board.devices.form.fuel-expense-non-urban') }}
              </label>
              <input
                  id="vehicleFuelExpenseNonUrban"
                  type="number"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('fuelExpenseNonUrban') === false}"
                  v-model="fuelExpenseNonUrban"
                  :disabled="!editMode"
              />
            </div>
          </div>
          <div class="rows phone-cols">
            <div class="half">
              <label for="vehicleSeats">
                {{ $t('settings.on-board.devices.form.seats') }}
              </label>
              <input
                  id="vehicleSeats"
                  type="number"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('seats') === false}"
                  v-model="seats"
                  :disabled="!editMode"
              />
            </div>
            <div class="half">
              <label for="vehicleTankCapacity">
                {{ $t('settings.on-board.devices.form.tank-capacity') }}
              </label>
              <input
                  id="vehicleTankCapacity"
                  type="number"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('tankCapacity') === false}"
                  v-model="tankCapacity"
                  :disabled="!editMode"
              />
            </div>
          </div>
        </template>
        <div class="rows phone-cols">
          <template v-if="userCompany?.allowFullVehicleEdit">
            <div class="half">
              <label for="vehicleTripConstructionMethod">
                {{ $t('settings.on-board.devices.form.trip-construction-method') }}
              </label>
              <select
                  id="vehicleTripConstructionMethod"
                  class="stretch"
                  v-model="tripConstructionMethod"
                  :disabled="!editMode"
              >
                <option :value="TripConstructionMethod.Motion">
                  {{ $t('settings.on-board.devices.form.trip-construction-method.motion') }}
                </option>
                <option :value="TripConstructionMethod.Stops">
                  {{ $t('settings.on-board.devices.form.trip-construction-method.stops') }}
                </option>
              </select>
            </div>
            <div class="half">
              <label for="vehicleTripStopThresholdSeconds">
                {{ $t('settings.on-board.devices.form.trip-stop-threshold-seconds') }}
              </label>
              <input
                  id="vehicleTripStopThresholdSeconds"
                  type="number"
                  min="0"
                  step="1"
                  class="stretch"
                  v-model.number="tripStopThresholdSeconds"
                  :disabled="!editMode || tripConstructionMethod !== TripConstructionMethod.Stops"
              />
            </div>
          </template>
          <template v-else>
            <div class="half">
              <label>{{ $t('settings.on-board.devices.form.trip-construction-method') }}</label>
              <p>{{ tripConstructionMethodLabel }}</p>
            </div>
            <div class="half">
              <label>{{ $t('settings.on-board.devices.form.trip-stop-threshold-seconds') }}</label>
              <p>{{ formattedTripStopThresholdSeconds }}</p>
            </div>
          </template>
        </div>
        <div class="rows phone-cols">
          <div class="half">
            <label for="vehicleLIDue">
              {{ $t('settings.car-fleet.form.liability-insurance-date') }}
            </label>
            <Datepicker
                id="vehicleLIDue"
                :model-value="liabilityInsuranceDue"
                @update:modelValue="selectLiabilityInsuranceDue"
                :format="$t('settings.generic.date-format')"
                :locale="locale"
                :enable-time-picker="false"
                :min-date="new Date()"
                auto-apply
                clearable
                :state="showStateByField('liabilityInsuranceDue') || isDateOverdue(liabilityInsuranceDue)"
                :disabled="!editMode"
            />
          </div>
          <div class="half">
            <label for="vehicleLiabilityInsuranceNote">
              {{ $t('settings.car-fleet.form.liability-insurance-note') }}
            </label>
            <input
                id="vehicleLiabilityInsuranceNote"
                class="stretch"
                :class="{'is-invalid': showStateByField('liabilityInsuranceNote') === false}"
                v-model="liabilityInsuranceNote"
                :disabled="!editMode"
            />
          </div>
        </div>
        <div class="rows phone-cols">
          <div class="half">
            <label for="vehicleTIDue">
              {{ $t('settings.car-fleet.form.tech-inspection-date') }}
            </label>
            <Datepicker
                id="vehicleTIDue"
                :model-value="techInspectionDue"
                @update:modelValue="selectTechInspectionDue"
                :format="$t('settings.generic.date-format')"
                :locale="locale"
                :enable-time-picker="false"
                :min-date="new Date()"
                auto-apply
                clearable
                :state="showStateByField('techInspectionDue') || isDateOverdue(techInspectionDue)"
                :disabled="!editMode"
            />
          </div>
          <div class="half">
            <label for="vehicleTechInspectionNote">
              {{ $t('settings.car-fleet.form.tech-inspection-note') }}
            </label>
            <input
                id="vehicleTechInspectionNote"
                class="stretch"
                :class="{'is-invalid': showStateByField('techInspectionNote') === false}"
                v-model="techInspectionNote"
                :disabled="!editMode"
            />
          </div>
        </div>
        <div class="rows phone-cols">
          <div class="half">
            <label for="vehicleVignetteDue">
              {{ $t('settings.car-fleet.form.vignette-date') }}
            </label>
            <Datepicker
                id="vehicleVignetteDue"
                :model-value="vignetteDue"
                @update:modelValue="selectVignetteDue"
                :format="$t('settings.generic.date-format')"
                :locale="locale"
                :enable-time-picker="false"
                :min-date="new Date()"
                auto-apply
                clearable
                :state="showStateByField('vignetteDue') || isDateOverdue(vignetteDue)"
                :disabled="!editMode"
            />
          </div>
          <div class="half">
            <label for="vehicleVignetteNote">
              {{ $t('settings.car-fleet.form.vignette-note') }}
            </label>
            <input
                id="vehicleVignetteNote"
                class="stretch"
                :class="{'is-invalid': showStateByField('vignetteNote') === false}"
                v-model="vignetteNote"
                :disabled="!editMode"
            />
          </div>
        </div>
        <template v-if="userCompany?.allowFullVehicleEdit">
          <div class="mb-10 rows">
            <div class="width-50p">
              <fieldset class="rows">
                <legend>{{ $t('dashboard.quick-map.info-window.engine-hours') }}</legend>
                <div class="engine-time-hours-field">
                  <label for="engineHours">
                    {{ $t('generic.hours') }}
                  </label>
                  <input
                      id="engineHours"
                      type="number"
                      class="stretch"
                      :class="{'is-invalid': showStateByField('engineHours') === false}"
                      v-model="engineHours"
                      @update:model-value="syncEngineMinutes"
                      :disabled="!editMode || !engineHoursSupported"
                  />
                </div>
                <div class="engine-time-equals-symbol">=</div>
                <div>
                  <label for="engineMinutes">
                    {{ $t('generic.minutes') }}
                  </label>
                  <input
                      id="engineMinutes"
                      type="number"
                      class="stretch"
                      :class="{'is-invalid': showStateByField('engineMinutes') === false}"
                      v-model="engineMinutes"
                      @update:model-value="syncEngineHours"
                      :disabled="!editMode || !engineHoursSupported"
                  />
                </div>
              </fieldset>
            </div>
            <div class="width-50p">
              <fieldset class="cols">
                <legend>{{ $t('settings.car-fleet.form.odometer') }}</legend>
                <div>
                  <label for="odometer">
                    {{ $t('settings.car-fleet.form.odometer') }}
                  </label>
                  <input
                      id="odometer"
                      type="number"
                      class="stretch"
                      :class="{'is-invalid': showStateByField('odometer') === false}"
                      v-model="odometerData.odometer"
                      :disabled="!editMode || !isOdometerEditable"
                  />
                </div>
              </fieldset>
            </div>
          </div>
          <div class="rows phone-cols mb-10">
            <div class="half">
              <input
                  id="vehicleFuelCalculated"
                  type="checkbox"
                  v-model="fuelCalculated"
                  class="big-check-box"
                  :disabled="!editMode"
              />
              <label for="vehicleFuelCalculated">
                {{ $t('settings.on-board.devices.form.fuel-calculated') }}
              </label>
            </div>
            <div class="half">
              <input
                  id="vehicleOohSupported"
                  type="checkbox"
                  v-model="oohSupported"
                  class="big-check-box"
                  :disabled="!editMode"
              />
              <label for="vehicleOohSupported">
                {{ $t('settings.on-board.devices.form.ooh-supported') }}
              </label>
            </div>
          </div>
          <div class="rows phone-cols mb-10">
            <div class="half">
              <input
                  id="vehicleEngineHoursSupported"
                  type="checkbox"
                  v-model="engineHoursSupported"
                  class="big-check-box"
                  :disabled="!editMode"
              />
              <label for="vehicleEngineHoursSupported">
                {{ $t('settings.on-board.devices.form.engine-hours-supported') }}
              </label>
              <div class="readonly-label mt-5 pl-10">
                {{ $t('settings.on-board.devices.form.trip-minimum-distance') }}: {{ formattedTripMinimumDistance }}
              </div>
            </div>
            <div class="half">
              <input
                  id="vehicleMovingTimeSupported"
                  type="checkbox"
                  v-model="movingTimeSupported"
                  class="big-check-box"
                  :disabled="!editMode"
              />
              <label for="vehicleMovingTimeSupported">
                {{ $t('settings.on-board.devices.form.moving-time-supported') }}
              </label>
            </div>
          </div>
        </template>

        <div class="rows cancel-save-buttons">
         <div v-if="!editMode">
           <button class="button-save" @click="edit"
                   :disabled="selectedVehicleId == null">
             {{ $t('generic.edit') }}
           </button>
         </div>
          <div v-else>
            <button class="button-cancel no-highlighted" @click="cancelEdit">
              {{ $t('generic.cancel') }}
            </button>
            <button type="submit" class="button-save" @click="save">
              {{ $t('generic.save') }}
            </button>
          </div>
        </div>
        <fieldset>
          <legend>{{ $t('settings.car-fleet.form.auto-check') }}</legend>
          <div class="rows phone-cols">
            <div class="half">
              <label for="autoCheckVehicleIDue">
                {{ $t('settings.car-fleet.form.liability-insurance-date') }}
              </label>
              <Datepicker
                  id="autoCheckVehicleIDue"
                  :model-value="autoCheckLiabilityInsuranceDue"
                  :format="$t('settings.generic.date-format')"
                  :locale="locale"
                  :state="isDateOverdue(autoCheckLiabilityInsuranceDue)"
                  auto-apply
                  disabled
              />
            </div>
            <div class="half">
              <label for="autoCheckVehicleLiabilityInsuranceNote">
                {{ $t('settings.car-fleet.form.liability-insurance-note') }}
              </label>
              <input
                  id="autoCheckVehicleLiabilityInsuranceNote"
                  class="stretch"
                  v-model="autoCheckLiabilityInsuranceNote"
                  disabled
              />
            </div>
          </div>
          <div class="rows phone-cols" v-if="autoCheckLiabilityInsuranceDue !== null">
            <div class="full">
              <label>{{ $t('settings.car-fleet.form.liability-insurance-payments') }}</label>
              <div class="rows justify-space-around">
                <template v-for="(item, index) in autoCheckLiabilityInsurancePayments" v-bind:key="item">
                  <label>{{item}}</label>
                  <label v-if="index !== autoCheckLiabilityInsurancePayments.length - 1">|</label>
                </template>
              </div>
            </div>
          </div>
          <div class="rows phone-cols">
            <div class="half">
              <label for="autoCheckVehicleVignetteDue">
                {{ $t('settings.car-fleet.form.vignette-date') }}
              </label>
              <Datepicker
                  id="autoCheckVehicleVignetteDue"
                  :model-value="autoCheckVignetteDue"
                  :format="$t('settings.generic.date-format')"
                  :locale="locale"
                  :state="isDateOverdue(autoCheckVignetteDue)"
                  auto-apply
                  disabled
              />
            </div>
            <div class="half">
              <label for="autoCheckVehicleVignetteNote">
                {{ $t('settings.car-fleet.form.vignette-note') }}
              </label>
              <input
                  id="autoCheckVehicleVignetteNote"
                  class="stretch"
                  v-model="autoCheckVignetteNote"
                  disabled
              />
            </div>
          </div>
          <div class="rows"
               :class="selectedVehicleId !== null && violations !== null ? 'justify-space-between' : 'justify-end'">
            <div v-if="selectedVehicleId !== null && violations !== null" class="rows align-items-center">
              <h3 class="violations-label"
                  :class="hasVehicleViolations ? 'text-danger' : 'text-green'"
                  @click="hasVehicleViolations ? showViolations() : showViolationsSourceInfo()">
                {{ $t('settings.car-fleet.form.available-violations')}}: {{ $t(hasVehicleViolations ? 'generic.yes' : 'generic.no') }}
              </h3>
              <button class="button-save" @click="showViolations" v-if="hasVehicleViolations">
                {{ $t('settings.car-fleet.form.show-violations') }}
              </button>
              <button class="button-save" @click="showViolationsSourceInfo" v-else>
                {{ $t('generic.information') }}
              </button>
            </div>
            <button class="button-save" :disabled="selectedVehicleId == null" @click="checkTaxes">
              {{ $t('settings.car-fleet.form.check') }}
            </button>
          </div>
        </fieldset>
        <template v-if="!userCompany?.allowFullVehicleEdit">
          <div class="rows phone-cols">
            <div class="half">
              <label for="vehicleLicensePlate">
                {{ $t('settings.car-fleet.form.vehicle-license-plate') }}
              </label>
              <input
                  id="vehicleLicensePlate"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('licensePlate') === false}"
                  v-model="licensePlate"
                  disabled
              />
            </div>
            <div class="half">
              <label for="vehicleDriverName">
                {{ $t('settings.car-fleet.form.vehicle-driver-name') }}
              </label>
              <input
                  id="vehicleDriverName"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('driverName') === false}"
                  v-model="driverName"
                  disabled
              />
            </div>
          </div>
          <div class="rows phone-cols">
            <div class="half">
              <label for="vehicleMake">
                {{ $t('settings.on-board.devices.form.make') }}
              </label>
              <select
                  id="vehicleMake"
                  :class="['stretch', {'is-invalid': showStateByField('make') === false}]"
                  v-model="make"
                  disabled
              >
                <option :value="null">{{ $t('settings.generic.select-placeholder') }}</option>
                <option v-for="make in makesAlphabetical" v-bind:key="make.id" :value="make.id">
                  {{ make.name }}
                </option>
              </select>
            </div>
            <div class="half">
              <label for="vehicleModel">
                {{ $t('settings.on-board.devices.form.model') }}
              </label>
              <select
                  id="vehicleModel"
                  :class="['stretch', {'is-invalid': showStateByField('model') === false}]"
                  v-model="model"
                  disabled
              >
                <option :value="null">{{ $t('settings.generic.select-placeholder') }}</option>
                <option v-for="model in filteredModels" v-bind:key="model.id" :value="model.id">
                  {{ model.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="rows phone-cols">
            <div class="half">
              <label for="vehicleFuelExpenseUrban">
                {{ $t('settings.on-board.devices.form.fuel-expense-urban') }}
              </label>
              <input
                  id="vehicleFuelExpenseUrban"
                  type="number"
                  step="0.1"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('fuelExpenseUrban') === false}"
                  v-model="fuelExpenseUrban"
                  disabled
              />
            </div>
            <div class="half">
              <label for="vehicleFuelExpenseNonUrban">
                {{ $t('settings.on-board.devices.form.fuel-expense-non-urban') }}
              </label>
              <input
                  id="vehicleFuelExpenseNonUrban"
                  type="number"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('fuelExpenseNonUrban') === false}"
                  v-model="fuelExpenseNonUrban"
                  disabled
              />
            </div>
          </div>
          <div class="rows phone-cols mb-10">
            <div class="half">
              <label for="vehicleSeats">
                {{ $t('settings.on-board.devices.form.seats') }}
              </label>
              <input
                  id="vehicleSeats"
                  type="number"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('seats') === false}"
                  v-model="seats"
                  disabled
              />
            </div>
            <div class="half">
              <label for="vehicleTankCapacity">
                {{ $t('settings.on-board.devices.form.tank-capacity') }}
              </label>
              <input
                  id="vehicleTankCapacity"
                  type="number"
                  class="stretch"
                  :class="{'is-invalid': showStateByField('tankCapacity') === false}"
                  v-model="tankCapacity"
                  disabled
              />
            </div>
          </div>
          <div class="mb-10 rows">
            <div class="width-50p">
              <fieldset class="rows">
                <legend>{{ $t('dashboard.quick-map.info-window.engine-hours') }}</legend>
                <div class="engine-time-hours-field">
                  <label for="engineHours">
                    {{ $t('generic.hours') }}
                  </label>
                  <input
                      id="engineHours"
                      type="number"
                      class="stretch"
                      v-model="engineHours"
                      disabled
                  />
                </div>
                <div class="engine-time-equals-symbol">=</div>
                <div>
                  <label for="engineMinutes">
                    {{ $t('generic.minutes') }}
                  </label>
                  <input
                      id="engineMinutes"
                      type="number"
                      class="stretch"
                      v-model="engineMinutes"
                      disabled
                  />
                </div>
              </fieldset>
            </div>
            <div class="width-50p">
              <fieldset class="cols">
                <legend>{{ $t('settings.car-fleet.form.odometer') }}</legend>
                <div>
                  <label for="odometer">
                    {{ $t('settings.car-fleet.form.odometer') }}
                  </label>
                  <input
                      id="odometer"
                      type="number"
                      class="stretch"
                      v-model="odometerData.odometer"
                      disabled
                  />
                </div>
              </fieldset>
            </div>
          </div>
          <div class="rows phone-cols mb-10">
            <div class="half">
              <input
                  id="vehicleFuelCalculated"
                  type="checkbox"
                  v-model="fuelCalculated"
                  class="big-check-box"
                  disabled
              />
              <label for="vehicleFuelCalculated">
                {{ $t('settings.on-board.devices.form.fuel-calculated') }}
              </label>
            </div>
            <div class="half">
              <input
                  id="vehicleOohSupported"
                  type="checkbox"
                  v-model="oohSupported"
                  class="big-check-box"
                  disabled
              />
              <label for="vehicleOohSupported">
                {{ $t('settings.on-board.devices.form.ooh-supported') }}
              </label>
            </div>
          </div>
          <div class="rows phone-cols mb-10">
            <div class="half">
              <input
                  id="vehicleEngineHoursSupported"
                  type="checkbox"
                  v-model="engineHoursSupported"
                  class="big-check-box"
                  disabled
              />
              <label for="vehicleEngineHoursSupported">
                {{ $t('settings.on-board.devices.form.engine-hours-supported') }}
              </label>
              <div class="readonly-label mt-5">
                {{ $t('settings.on-board.devices.form.trip-minimum-distance') }}: {{ formattedTripMinimumDistance }}
              </div>
            </div>
            <div class="half">
              <input
                  id="vehicleMovingTimeSupported"
                  type="checkbox"
                  v-model="movingTimeSupported"
                  class="big-check-box"
                  disabled
              />
              <label for="vehicleMovingTimeSupported">
                {{ $t('settings.on-board.devices.form.moving-time-supported') }}
              </label>
            </div>
          </div>
        </template>
        <fieldset class="immobilizer-fieldset width-50p">
          <legend>{{ $t('settings.car-fleet.form.immobilizer') }}</legend>
          <div class="rows justify-center">
            <button class="mr-10"
                    type="button"
                    :disabled="selectedVehicleId == null"
                    @click="enableImmobilizer">
              {{ $t('settings.car-fleet.form.enable-immobilizer') }}
            </button>
            <button type="button"
                    :disabled="selectedVehicleId == null"
                    @click="disableImmobilizer">
              {{ $t('settings.car-fleet.form.disable-immobilizer') }}
            </button>
          </div>
        </fieldset>
      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from '@vuepic/vue-datepicker';
import locale from '@/assets/js/locale';
import {mapGetters, mapState} from 'vuex';
import {dataAggregator, dataManipulator, entityDataManager} from '@/assets/js/data-manipulation';
import {actions} from '@/assets/js/vue/vuex/actions';
import notification from '@/assets/js/notification';
import DataGrid from '@/components/pages/partials/DataGrid';
import notifications from '@/assets/js/notification';
import moment from "moment";
import {popups} from "@/assets/js/popups";
import {TripConstructionMethod} from '@/assets/js/enums/TripConstructionMethod.ts';

export default {
  components: {
    DataGrid,
    Datepicker,
  },
  name: 'ManageCarFleet',
  props: {
    searchToken: String,
  },
  data() {
    return {
      locale: locale.getLocale(),
      fieldStates: {},
      editMode: false,

      // editable fields
      selectedVehicleId: null,
      nickname: null,
      liabilityInsuranceDue: null,
      liabilityInsuranceNote: null,
      techInspectionDue: null,
      techInspectionNote: null,
      vignetteDue: null,
      vignetteNote: null,
      autoCheckLiabilityInsuranceDue: null,
      autoCheckLiabilityInsuranceNote: null,
      autoCheckLiabilityInsurancePayments: null,
      autoCheckVignetteDue: null,
      autoCheckVignetteNote: null,

      // readonly fields
      licensePlate: null,
      driverName: null,
      make: null,
      model: null,
      fuelExpenseUrban: null,
      fuelExpenseNonUrban: null,
      seats: null,
      initialEngineMinutes: null,
      engineHours: null,
      engineMinutes: null,
      engineTimeInitializedVehicleId: null,
      odometerInitializedVehicleId: null,
      initialOdometerData: {},
      odometerData: {},
      tankCapacity: null,
      fuelCalculated: null,
      oohSupported: null,
      engineHoursSupported: null,
      tripMinimumDistance: null,
      TripConstructionMethod,
      tripConstructionMethod: TripConstructionMethod.Motion,
      tripStopThresholdSeconds: null,
      movingTimeSupported: null,
      identifier: null,
    };
  },
  computed: {
    ...mapState(['vehicles', 'devices', 'lastErrorDetails', 'violations', 'vehicles', 'positions']),
    ...mapGetters(['makesAlphabetical', 'modelsAlphabetical', 'hasViolations', 'userCompany']),
    dataGridModel() {
      return {
        headers: {
          [this.$t('settings.car-fleet.vehicle-name')]: 'vehicle',
          [this.$t('settings.car-fleet.form.device')]: 'device',
        },
        values: {
          vehicle: this.getVehicleTitle,
          device: this.getVehicleDeviceName,
        },
      };
    },
    filteredVehicles() {
      return dataAggregator.filterVehiclesByToken(this.vehicles, this.devices, this.searchToken);
    },
    filteredModels() {
      return this.make === null ?
          this.modelsAlphabetical :
          this.modelsAlphabetical.filter(model => model.make === this.make);
    },
    hasVehicleViolations() {
      return this.vehicleViolations?.length > 0;
    },
    vehicleViolations() {
      return dataAggregator.filterViolationsByLicensePlate(this.violations, this.licensePlate);
    },
    isOdometerEditable() {
      const vehicle = entityDataManager.getById(this.vehicles, this.selectedVehicleId);
      const device = entityDataManager.getDeviceByVehicle(this.devices, vehicle);

      return device.model !== 'FMB003' && device.model !== 'FMB140';
    },
    formattedTripMinimumDistance() {
      return this.tripMinimumDistance ?? '-';
    },
    tripConstructionMethodLabel() {
      return this.tripConstructionMethod === TripConstructionMethod.Stops
          ? this.$t('settings.on-board.devices.form.trip-construction-method.stops')
          : this.$t('settings.on-board.devices.form.trip-construction-method.motion');
    },
    formattedTripStopThresholdSeconds() {
      return this.tripStopThresholdSeconds ?? this.$t('settings.on-board.devices.form.trip-stop-threshold-seconds.default');
    }
  },
  methods: {
    selectVehicleId(vehicleId) {
      const vehicle = entityDataManager.getById(this.vehicles, vehicleId);
      this.$router.push({
        query: {
          id: vehicle.id
        }
      });
      this.selectedVehicleId = vehicle.id;
    },
    loadDataByLicensePlate(vehicleLicensePlate) {
      const userPassedVehicleId = entityDataManager.getVehicleIdByLicensePlate(this.vehicles, vehicleLicensePlate);
      if (userPassedVehicleId == null) {
        return;
      }

      this.cancelEdit(false);
      this.selectedVehicleId = userPassedVehicleId;
      this.fillFormByVehicleId(userPassedVehicleId);
    },
    loadDataById(vehicleId) {
      const parsedVehicleId = parseInt(vehicleId);
      if (!parsedVehicleId) {
        return;
      }

      this.cancelEdit(false);
      this.selectedVehicleId = parsedVehicleId;
      this.fillFormByVehicleId(parsedVehicleId);
    },
    selectLiabilityInsuranceDue(value) {
      this.liabilityInsuranceDue = value != null ? dataManipulator.dateToString(value) : null;
    },
    selectTechInspectionDue(value) {
      this.techInspectionDue = value != null ? dataManipulator.dateToString(value) : null;
    },
    selectVignetteDue(value) {
      this.vignetteDue = value != null ? dataManipulator.dateToString(value) : null;
    },
    loadPage() {
      if (this.$route.query.licensePlate) {
        const userPassedLicensePlate = this.$route.query.licensePlate;
        this.loadDataByLicensePlate(userPassedLicensePlate);
      }
      else if (this.$route.query.id) {
        this.loadDataById(this.$route.query.id);
      }
      else {
        this.fillFormByVehicleId(null);
      }
    },
    fillFormByVehicleId(vehicleId) {
      this.updateAutoCheckFields(vehicleId);

      if (vehicleId === null) {
        this.nickname = null;
        this.liabilityInsuranceNote = null;
        this.techInspectionNote = null;
        this.vignetteNote = null;
        this.licensePlate = null;
        this.driverName = null;
        this.fuelExpenseUrban = null;
        this.fuelExpenseNonUrban = null;
        this.seats = null;
        this.initialEngineMinutes = null;
        this.engineHours = null;
        this.engineMinutes = null;
        this.engineTimeInitializedVehicleId = null;
        this.odometerInitializedVehicleId = null;
        this.odometerData = {};
        this.initialOdometerData = {};
        this.tankCapacity = null;
        this.fuelCalculated = null;
        this.oohSupported = null;
        this.engineHoursSupported = null;
        this.tripMinimumDistance = null;
        this.tripConstructionMethod = TripConstructionMethod.Motion;
        this.tripStopThresholdSeconds = null;
        this.movingTimeSupported = null;
        this.identifier = null;


        this.selectLiabilityInsuranceDue(null);
        this.selectTechInspectionDue(null);
        this.selectVignetteDue(null);

        return;
      }

      const vehicle = entityDataManager.getById(this.vehicles, vehicleId);
      const model = entityDataManager.getModelByVehicle(this.modelsAlphabetical, vehicle);
      const make = entityDataManager.getMakeByModel(this.makesAlphabetical, model);
      const device = entityDataManager.getDeviceByVehicle(this.devices, vehicle);

      if (vehicle !== null) {
        this.engineTimeInitializedVehicleId = null;
        this.odometerInitializedVehicleId = null;
        this.nickname = vehicle.nickname;
        this.liabilityInsuranceNote = vehicle.liabilityInsuranceNote;
        this.techInspectionNote = vehicle.techInspectionNote;
        this.vignetteNote = vehicle.vignetteNote;
        this.licensePlate = vehicle.licensePlate;
        this.driverName = vehicle.driverName;
        this.fuelExpenseUrban = vehicle.fuelExpenseUrban;
        this.fuelExpenseNonUrban = vehicle.fuelExpenseNonUrban;
        this.seats = vehicle.seats;
        this.initializePositionData();
        this.tankCapacity = vehicle.tankCapacity;
        this.fuelCalculated = vehicle.fuelCalculated;
        this.oohSupported = vehicle.oohSupported;
        this.engineHoursSupported = vehicle.engineHoursSupported;
        this.tripMinimumDistance = vehicle.tripMinimumDistance ?? null;
        this.tripConstructionMethod = vehicle.tripConstructionMethod ?? TripConstructionMethod.Motion;
        this.tripStopThresholdSeconds = vehicle.tripStopThresholdSeconds ?? null;
        this.movingTimeSupported = vehicle.movingTimeSupported;
        this.identifier = device.uniqueId;

        if (make) {
          this.make = make.id;
        }

        if (model) {
          this.model = model.id;
        }

        this.selectLiabilityInsuranceDue(
            vehicle.liabilityInsuranceDue !== null ? new Date(vehicle.liabilityInsuranceDue) : null,
        );
        this.selectTechInspectionDue(
            vehicle.techInspectionDue !== null ? new Date(vehicle.techInspectionDue) : null,
        );
        this.selectVignetteDue(
            vehicle.vignetteDue !== null ? new Date(vehicle.vignetteDue) : null,
        );
      }
    },
    updateAutoCheckFields(vehicleId) {
      if (vehicleId === null) {
        this.autoCheckLiabilityInsuranceDue = null;
        this.autoCheckLiabilityInsuranceNote = null;
        this.autoCheckLiabilityInsurancePayments = null;
        this.autoCheckVignetteDue = null;
        this.autoCheckVignetteNote = null;
        return;
      }

      const vehicle = entityDataManager.getById(this.vehicles, vehicleId);

      if (vehicle !== null) {
        const autoCheckLiabilityInsuranceDueDate = new Date(vehicle.autoCheckLiabilityInsuranceDue);
        this.autoCheckLiabilityInsuranceDue = dataManipulator.dateToString(
            vehicle.autoCheckLiabilityInsuranceDue !== null ? autoCheckLiabilityInsuranceDueDate : null);
        this.autoCheckLiabilityInsuranceNote = vehicle.autoCheckLiabilityInsuranceNote;
        this.autoCheckLiabilityInsurancePayments = [];
        for (let i = 4; i--; i >= 1) {
          const payment = moment(autoCheckLiabilityInsuranceDueDate).subtract(i * 3, 'months');
          this.autoCheckLiabilityInsurancePayments.push(payment.format(this.$t('settings.generic.date-format-moment')));
        }
        this.autoCheckVignetteDue = dataManipulator.dateToString(
            vehicle.autoCheckVignetteDue !== null ? new Date(vehicle.autoCheckVignetteDue) : null);
        this.autoCheckVignetteNote = vehicle.autoCheckVignetteNote;
      }
    },
    initializePositionData() {
      const vehicle = entityDataManager.getById(this.vehicles, this.selectedVehicleId);
      if (!vehicle || this.editMode || (
          this.engineTimeInitializedVehicleId === vehicle.id &&
          this.odometerInitializedVehicleId === vehicle.id
      )) {
        return;
      }

      const device = entityDataManager.getDeviceByVehicle(this.devices, vehicle);
      const devicePosition = this.positions.filter(position => position.deviceId === device.id)[0] || null;
      if (!devicePosition) {
        return;
      }

      const engineTime = entityDataManager.getEngineTimeByPosition(devicePosition);
      if (this.engineTimeInitializedVehicleId !== vehicle.id && engineTime !== null && engineTime !== undefined) {
        this.engineMinutes = dataManipulator.convertMillisecondsToMinutes(engineTime);
        this.syncEngineHours();
        this.initialEngineMinutes = this.engineMinutes;
        this.engineTimeInitializedVehicleId = vehicle.id;
      }

      const totalDistance = entityDataManager.getPositionAttribute(devicePosition, 'totalDistance', null);
      const numericTotalDistance = Number(totalDistance);
      if (this.odometerInitializedVehicleId !== vehicle.id &&
          totalDistance !== null &&
          totalDistance !== undefined &&
          Number.isFinite(numericTotalDistance)) {
        this.odometerData = {
          odometer: Math.round((numericTotalDistance / 1000) * 100) / 100
        };
        this.initialOdometerData = dataManipulator.deepCopyObject(this.odometerData);
        this.odometerInitializedVehicleId = vehicle.id;
      }
    },
    syncEngineMinutes() {
      this.engineMinutes = dataManipulator.convertHoursToMinutes(this.engineHours);
    },
    syncEngineHours() {
      this.engineHours = dataManipulator.convertMinutesToHours(this.engineMinutes);
    },
    showStateByField(field) {
      return entityDataManager.getStateByField(this.fieldStates, field);
    },
    getVehicleTitle(vehicle) {
      return vehicle.fullName;
    },
    getVehicleDeviceName(vehicle) {
      const device = entityDataManager.getDeviceByVehicle(this.devices, vehicle);

      return device !== null ? device.name : '';
    },
    isDateOverdue(date) {
      if (!date) {
        return null;
      }

      const parsedDate = dataManipulator.iso8601DateStringToLocalMoment(date);
      const currentDate = moment().local();
      // invalid parsed date or the date is not due
      if (!parsedDate.isValid() || currentDate <= parsedDate) {
        return null;
      }

      return false;
    },
    showDirtyFields() {
      this.fieldStates = {};

      if (this.lastErrorDetails === null) {
        return;
      }

      this.lastErrorDetails.forEach(error => {
        if (error.field !== undefined) {
          this.fieldStates[error.field] = false;
        }
      });
    },
    edit() {
      this.editMode = true;
    },
    cancelEdit(resetForm = true) {
      if (resetForm) {
        this.fillFormByVehicleId(this.selectedVehicleId);
      }
      this.editMode = false;
      this.fieldStates = {};
      this.$forceUpdate();
    },
    save() {
      if (this.selectedVehicleId === null) {
        notifications.error(this.$t('settings.car-fleet.vehicle-required'));
        return;
      }

      const payload = {
        id: this.selectedVehicleId,
        nickname: this.nickname,
        liabilityInsuranceDue: this.liabilityInsuranceDue,
        liabilityInsuranceNote: this.liabilityInsuranceNote,
        techInspectionDue: this.techInspectionDue,
        techInspectionNote: this.techInspectionNote,
        vignetteDue: this.vignetteDue,
        vignetteNote: this.vignetteNote,
        tripMinimumDistance: this.tripMinimumDistance,
        tripConstructionMethod: this.tripConstructionMethod,
        tripStopThresholdSeconds: this.tripStopThresholdSeconds === '' ? null : this.tripStopThresholdSeconds,
      };

      if (this.userCompany.allowFullVehicleEdit) {
        payload.licensePlate = this.licensePlate
        payload.driverName = this.driverName;
        payload.make = this.make;
        payload.model = this.model;
        payload.fuelExpenseUrban = this.fuelExpenseUrban;
        payload.fuelExpenseNonUrban = this.fuelExpenseNonUrban;
        payload.seats = this.seats;
        payload.tankCapacity = this.tankCapacity;
        payload.fuelCalculated = this.fuelCalculated;
        payload.oohSupported = this.oohSupported;
        payload.engineHoursSupported = this.engineHoursSupported;
        payload.movingTimeSupported = this.movingTimeSupported;
      }

      const vehicle = entityDataManager.getById(this.vehicles, this.selectedVehicleId);
      const fetchViolations = vehicle.licensePlate !== payload.licensePlate &&
          entityDataManager.shouldFetchViolations(this.userCompany);
      const engineHoursChanged = this.userCompany?.allowFullVehicleEdit &&
          this.engineHoursSupported && this.initialEngineMinutes !== this.engineMinutes;
      const odometerChanged = this.userCompany?.allowFullVehicleEdit && this.isOdometerEditable &&
          !dataAggregator.compareObjects(this.initialOdometerData, this.odometerData) && this.odometerData;

      this.$store.dispatch(actions.EDIT_VEHICLE, payload)
          .then(() => {
            if (!engineHoursChanged && !odometerChanged) {
              return Promise.resolve();
            }

            const accumulatorsPayload = {deviceId: vehicle.deviceId};
            if (engineHoursChanged) {
              accumulatorsPayload.minutes = this.engineMinutes;
            }
            if (odometerChanged) {
              accumulatorsPayload.totalDistance = Math.round(this.odometerData.odometer * 1000);
            }

            return this.$store.dispatch(actions.SET_DEVICE_ACCUMULATORS, accumulatorsPayload);
          })
          .then(() => {
            if (odometerChanged) {
              const odometerPayload = {
                id: this.selectedVehicleId,
                odometer: this.odometerData.odometer
              };

              const messageTitle = this.$t('settings.car-fleet.form.sync-odometer');
              const messageDescription = this.$t('generic.please-wait-a-few-minutes');
              return this.$store.dispatch(actions.SET_VEHICLE_ODOMETER, odometerPayload)
                  .then(() => popups.information(this.$swal, messageTitle, messageDescription));
            }

            return Promise.resolve();
          })
          .then(() => {
            this.$store.dispatch(actions.GET_VEHICLES)
                .then(() => {
                  this.initializePositionData();
                });
            this.cancelEdit(false);
            this.initializePositionData();
            notification.success(this.$t('generic.save.success'));
            if (fetchViolations) {
              this.$store.dispatch(actions.CHECK_VIOLATIONS)
                  .catch(e => notification.error(this.$t(e.message)));
            }
          })
          .catch(e => notification.error(this.$t(e.message)))
          .finally(() => this.showDirtyFields());
    },
    checkTaxes() {
      if (this.selectedVehicleId === null) {
        return;
      }

      const payload = {id: this.selectedVehicleId};
      this.$store.dispatch(actions.CHECK_VEHICLE_TAXES, payload)
          .then(() => {
            notification.success(this.$t('settings.car-fleet.check-vehicle-taxes-success'));
            this.updateAutoCheckFields(this.selectedVehicleId);
          })
          .catch(e => notification.error(this.$t(e.message)));
    },
    enableImmobilizer() {
      this.$store.dispatch(actions.ENABLE_VEHICLE_IMMOBILIZER, {id: this.selectedVehicleId})
          .then(() => notification.success(this.$t('settings.car-fleet.enable-immobilizer-success')))
          .catch(e => notification.error(this.$t(e.message)));
    },
    disableImmobilizer() {
      this.$store.dispatch(actions.DISABLE_VEHICLE_IMMOBILIZER, {id: this.selectedVehicleId})
          .then(() => notification.success(this.$t('settings.car-fleet.disable-immobilizer-success')))
          .catch(e => notification.error(this.$t(e.message)));
    },
    showViolationsSourceInfo() {
      popups.showViolationsSourceInfo(this.$swal, this.userCompany, this.licensePlate);
    },
    showViolations() {
      if (this.selectedVehicleId === null || !this.hasVehicleViolations) {
        return;
      }

      popups.showViolations(this.$swal, this.vehicleViolations, this.vehicles, this.userCompany, this.licensePlate);
    }
  },
  created() {
    this.unwatchVehiclesChange = this.$store.watch(
        (state) => state.vehicles,
        (newValue) => {
          if (newValue !== null && newValue.length > 0) {

            this.loadPage();
            this.unwatchVehiclesChange();
          }
        }
    );

    if (!this.userCompany) {
      return;
    }
  },
  unmounted() {
    this.unwatchVehiclesChange?.();
  },
  watch: {
    $route() {
      this.loadPage();
    },
    userCompany(newValue) {
      if (!newValue) {
        return;
      }

      this.loadPage();
    },
    positions() {
      this.initializePositionData();
    }
  }
};
</script>

<style scoped lang="scss">
.edit-button {
  position: absolute;
  right: 15px;
  color: #64656B;
  width: 40px;
  height: 30px;
}

.name-container {
  position: relative;
}

.violations-label {
  cursor: pointer;
  margin-bottom: 0;
  margin-top: 0;
  padding-left: 10px;
  padding-right: 10px;
}

.engine-time-hours-field {
  margin-right: -5px;
}

.engine-time-equals-symbol {
  padding-top: 35px;
}

.immobilizer-fieldset {
  align-self: flex-end;
}
</style>
